import { useEffect, useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { EscalationMetricCard } from "@/components/domain/escalation-metric-card"
import { EscalationCategoryDonut } from "@/components/domain/escalation-category-donut"
import { EscalationOverTimeChart } from "@/components/domain/escalation-over-time-chart"
import { EscalationDetailTable } from "@/components/domain/escalation-detail-table"
import type { AnalyticsEscalationsProps } from "@/types/analytics_escalations"

// /analytics/escalations
//
// Mirrors the existing Topics/Index dropdown + Inertia partial-reload pattern.
// Filters and date range live in the URL query string so the page is
// shareable and the controller can recompute server-side stats. The
// over-time mode toggle and donut category filter are local UI state —
// the donut filter ALSO syncs into the URL so deep-linking lands on the
// right slice.

const ONLY = ["cards", "by_category", "over_time", "details", "filter"] as const

const REFRESH_MS = 30_000

export default function AnalyticsEscalationsIndex(props: AnalyticsEscalationsProps) {
  const { agents, selectedAgentId, cards, by_category, over_time, details, filter } = props

  const [mode, setMode] = useState<"count" | "percent">("count")
  const [autoRefresh, setAutoRefresh] = useState(false)
  const [localQuery, setLocalQuery] = useState(filter.q ?? "")

  // Auto-refresh polls the same URL via Inertia partial reload. Only the
  // data props update — chart components get fresh `buckets` references
  // and re-render naturally.
  useEffect(() => {
    if (!autoRefresh) return
    const id = window.setInterval(() => {
      router.reload({ only: [...ONLY] })
    }, REFRESH_MS)
    return () => window.clearInterval(id)
  }, [autoRefresh])

  const visit = (next: Partial<typeof filter> & { agent_id?: number }) => {
    const params: Record<string, string | number> = {
      agent_id: next.agent_id ?? selectedAgentId,
      from: next.from ?? filter.from,
      to: next.to ?? filter.to,
    }
    const cat = next.category !== undefined ? next.category : filter.category
    const out = next.outcome !== undefined ? next.outcome : filter.outcome
    const q = next.q !== undefined ? next.q : filter.q
    if (cat) params.category = cat
    if (out) params.outcome = out
    if (q) params.q = q

    router.visit("/analytics/escalations", {
      data: params,
      only: [...ONLY],
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleAgentChange = (id: number) => {
    router.get("/analytics/escalations", { agent_id: id }, { preserveScroll: true, preserveState: false })
  }

  const handleCategoryChange = (key: string | null) => {
    visit({ category: key })
  }

  const handleOutcomeChange = (outcome: string | null) => {
    visit({ outcome })
  }

  const handleQueryChange = (q: string) => {
    setLocalQuery(q)
  }

  // Debounce committing the search box into the URL so we don't spam the
  // backend on every keystroke.
  useEffect(() => {
    if (localQuery === (filter.q ?? "")) return
    const id = window.setTimeout(() => visit({ q: localQuery || null }), 350)
    return () => window.clearTimeout(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localQuery])

  const totalConvos = useMemo(() => over_time.reduce((acc, b) => acc + b.total_convos, 0), [over_time])

  return (
    <AppShell>
      <PageHeader
        eyebrow="Analytics"
        title="Escalations"
        subtitle={`${cards.total_escalations.value.toLocaleString()} escalations · ${totalConvos.toLocaleString()} conversations in window`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selectedAgentId}
              onChange={(e) => handleAgentChange(parseInt(e.target.value, 10))}
              className="rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
            >
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <input
              type="date"
              value={filter.from}
              onChange={(e) => visit({ from: e.target.value })}
              className="rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
            />
            <span className="text-sm text-app-muted">→</span>
            <input
              type="date"
              value={filter.to}
              onChange={(e) => visit({ to: e.target.value })}
              className="rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
            />
            <label className="flex items-center gap-2 rounded-control border border-border bg-white/5 px-3 py-2 text-xs text-app-muted">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
          </div>
        }
      />

      {/* Metric cards row */}
      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <EscalationMetricCard
          label="Total escalations"
          value={cards.total_escalations.value.toLocaleString()}
          deltaPct={cards.total_escalations.deltaPct}
          sparkline={cards.total_escalations.sparkline}
          tone="neutral"
        />
        <EscalationMetricCard
          label="Escalation rate"
          value={`${cards.escalation_rate.value.toFixed(1)}%`}
          deltaPct={cards.escalation_rate.deltaPct}
          sparkline={cards.escalation_rate.sparkline}
          tone="neutral"
        />
        <EscalationMetricCard
          label="Avg time to escalate"
          value={fmtDuration(cards.avg_time_to_escalate_seconds.value)}
          deltaPct={cards.avg_time_to_escalate_seconds.deltaPct}
          sparkline={cards.avg_time_to_escalate_seconds.sparkline}
          tone="neutral"
        />
        <EscalationMetricCard
          label="Rage escalations"
          value={cards.rage_escalations.value.toLocaleString()}
          deltaPct={cards.rage_escalations.deltaPct}
          sparkline={cards.rage_escalations.sparkline}
          tone="danger"
        />
        <EscalationMetricCard
          label="Repeat escalations"
          value={cards.repeat_escalations.value.toLocaleString()}
          deltaPct={cards.repeat_escalations.deltaPct}
          sparkline={cards.repeat_escalations.sparkline}
          tone="neutral"
        />
        <EscalationMetricCard
          label="CSAT after escalation"
          value={cards.csat_after_escalation.good + cards.csat_after_escalation.bad === 0
            ? "—"
            : `${cards.csat_after_escalation.percent.toFixed(0)}%`}
          deltaPct={cards.csat_after_escalation.deltaPct}
          sparkline={cards.csat_after_escalation.sparkline}
          tone="success"
        />
      </section>

      {/* Donut + Over-time */}
      <section className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <EscalationCategoryDonut
            buckets={by_category}
            selectedKey={filter.category}
            onSelect={handleCategoryChange}
          />
        </div>
        <div className="lg:col-span-2">
          <EscalationOverTimeChart
            buckets={over_time}
            mode={mode}
            onModeChange={setMode}
          />
        </div>
      </section>

      {/* Detail table */}
      <section>
        <EscalationDetailTable
          rows={details}
          categories={by_category}
          categoryFilter={filter.category}
          onCategoryChange={handleCategoryChange}
          outcomeFilter={filter.outcome}
          onOutcomeChange={handleOutcomeChange}
          query={localQuery}
          onQueryChange={handleQueryChange}
        />
      </section>
    </AppShell>
  )
}

function fmtDuration(sec: number): string {
  if (!sec || sec <= 0) return "—"
  if (sec < 60) return `${Math.round(sec)}s`
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`
  const h = Math.floor(m / 60)
  const mr = m % 60
  return mr ? `${h}h ${mr}m` : `${h}h`
}
